import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import type { Brand } from "../theme/brand";
import { AccountClient } from "../api/account.client";
import { avatarSrc, initials, languageOf } from "../api/account";
import { Avatar, Badge, Button, LanguageSelector, ThemeToggle, cn } from "../ui";
import { pruneTableState } from "../table/tableState";
import { JobBell, JobCenterDialog } from "./jobs/JobCenter";
import { activeNavPath, visibleNav, type NavItem } from "./nav";
import { HeaderSlotProvider, useHeaderSlotRefs } from "./PageHeader";

const COLLAPSE_KEY = "dl.sidebar.collapsed";

export interface AppShellProps {
  brand: Brand;
  nav: NavItem[];
  children: ReactNode;
}

/**
 * The suite's application frame.
 *
 * Desktop gets a sidebar that collapses to an icon rail; mobile gets the same
 * sidebar as an off-canvas drawer. The top bar carries the current page's
 * title, subtitle, badges and menu (portalled in by `<PageHeader/>`) alongside
 * the session controls, so a page never renders its own header block.
 */
export function AppShell({ brand, nav, children }: AppShellProps) {
  const { hasView } = useAuth();
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const { slots, setTitleEl, setActionsEl } = useHeaderSlotRefs();
  const items = useMemo(() => visibleNav(nav, hasView), [nav, hasView]);

  // Resolved once for the whole tree: which link is active depends on the
  // other links, so no entry can decide it alone.
  const navPathname = useRouterState({ select: (state) => state.location.pathname });
  const activePath = useMemo(() => activeNavPath(items, navPathname), [items, navPathname]);

  // Grids remember their filters, selection and scroll while the user is inside
  // their own section, which is what makes opening a row and coming back feel
  // like returning rather than starting over. Leaving the section is the signal
  // to forget: this runs after the page that owned the grid has unmounted and
  // written its final snapshot, so it clears exactly the grids the new route
  // has nothing to do with.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    pruneTableState(pathname);
  }, [pathname]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // Preference is cosmetic; losing it is not worth failing over.
      }
      return next;
    });
  };

  return (
    <HeaderSlotProvider slots={slots} navigate={(to) => void navigate({ to })}>
      <div className="flex h-full">
        <Sidebar
          brand={brand}
          items={items}
          activePath={activePath}
          collapsed={collapsed}
          drawerOpen={drawerOpen}
          onNavigate={() => setDrawerOpen(false)}
          onCloseDrawer={() => setDrawerOpen(false)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-header shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t("navigation.open", "Open navigation")}
              className="text-ink-muted hover:text-ink lg:hidden"
            >
              <Menu className="size-5" />
            </button>

            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={
                collapsed
                  ? t("navigation.expand", "Expand navigation")
                  : t("navigation.collapse", "Collapse navigation")
              }
              title={
                collapsed
                  ? t("navigation.expand", "Expand navigation")
                  : t("navigation.collapse", "Collapse navigation")
              }
              aria-pressed={collapsed}
              className="hidden text-ink-faint hover:text-ink lg:inline-flex"
            >
              {collapsed ? <PanelLeftOpen className="size-4.5" /> : <PanelLeftClose className="size-4.5" />}
            </button>

            {/* Separates the shell's own control from the page's content, so
                the collapse toggle is not mistaken for part of the page. */}
            <span aria-hidden="true" className="mx-1 hidden h-5 w-px bg-line lg:block" />

            {/* Page title / subtitle / badges land here. */}
            <div ref={setTitleEl} className="min-w-0 flex-1" />

            {/* The page's own menu. */}
            <div ref={setActionsEl} className="flex shrink-0 items-center gap-2" />

            <SessionControls />
          </header>

          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</main>
          <JobCenterDialog />
        </div>
      </div>
    </HeaderSlotProvider>
  );
}

function SessionControls() {
  const { account, logout, canWrite, http } = useAuth();
  const { t } = useTranslation();
  const accounts = useMemo(() => new AccountClient(http), [http]);

  return (
    <div className="flex shrink-0 items-center gap-2 border-l border-line pl-2">
      {/* USER accounts see the whole UI but can't mutate; saying so once here
          beats disabled buttons with no explanation. */}
      {!canWrite && (
        <Badge variant="warning" size="sm" className="max-sm:hidden">
          {t("badge.read-only", "Read only")}
        </Badge>
      )}

      <JobBell />
      <LanguageSelector
        onChange={(locale) => {
          // Mirrored onto the account so this and the settings page agree; a
          // failure here only costs portability, never the switch itself.
          if (account) {
            void accounts
              .updateProfile(account.id, { language: languageOf(locale) })
              .catch(() => undefined);
          }
        }}
      />
      <ThemeToggle />

      {/* The picture is the link to the account page. A name in a header is
          decoration; a picture is the affordance people already reach for, and
          it frees the width that the longer email addresses were eating. */}
      <Link
        to="/account"
        title={account?.credentials.username}
        aria-label={t("account.title", "Your account")}
        className="rounded-full outline-offset-2 hover:opacity-85 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <Avatar src={avatarSrc(account?.data?.avatar)} initials={initials(account)} size="sm" />
      </Link>
      <Button
        variant="ghost"
        size="sm"
        icon={<LogOut className="size-4" />}
        onClick={() => void logout()}
        aria-label={t("navigation.item.logout", "Log out")}
      />
    </div>
  );
}

function Sidebar({
  brand,
  items,
  activePath,
  collapsed,
  drawerOpen,
  onNavigate,
  onCloseDrawer,
}: {
  brand: Brand;
  items: NavItem[];
  /** The single link the shell resolved as active. */
  activePath: string | undefined;
  /** Desktop rail mode. The mobile drawer always renders expanded. */
  collapsed: boolean;
  drawerOpen: boolean;
  onNavigate: () => void;
  onCloseDrawer: () => void;
}) {
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseDrawer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen, onCloseDrawer]);

  // The rail only applies on desktop; in the mobile drawer there's room for
  // labels, so it always renders expanded there.
  return (
    <>
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onCloseDrawer}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={cn(
          "z-40 flex shrink-0 flex-col border-r border-line bg-surface transition-[width]",
          collapsed ? "lg:w-14" : "lg:w-sidebar",
          "w-sidebar max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:transition-transform",
          drawerOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full",
        )}
      >
        <div
          className={cn(
            "flex h-header shrink-0 items-center gap-2 border-b border-line px-4 lg:px-3",
            // In the rail the mark is the whole identity, so centre it.
            collapsed && "lg:justify-center lg:px-0",
          )}
        >
          {brand.logo && <img src={brand.logo} alt="" className="size-6 shrink-0 rounded" />}
          <span
            className={cn(
              "truncate text-sm font-semibold tracking-tight text-ink",
              collapsed && "lg:hidden",
            )}
          >
            {brand.name}
          </span>
          <button
            type="button"
            onClick={onCloseDrawer}
            aria-label="Close navigation"
            className="ml-auto text-ink-faint hover:text-ink lg:hidden"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Scrolling clips the rail's flyouts, so the collapsed rail lets
            overflow show instead: it is icons only, so it fits without
            scrolling at any realistic nav length. */}
        <nav
          className={cn(
            "flex-1 p-2",
            collapsed ? "overflow-y-auto lg:overflow-visible" : "overflow-y-auto overflow-x-hidden",
          )}
        >
          <ul className="flex flex-col gap-0.5">
            {items.map((item) => (
              <NavEntry
                key={item.labelKey}
                item={item}
                activePath={activePath}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}

function NavEntry({
  item,
  collapsed,
  onNavigate,
  activePath,
  depth = 0,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
  /** The single link that should render as active, resolved by the shell. */
  activePath: string | undefined;
  depth?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const label = t(item.labelKey, item.labelKey);
  const Icon = item.icon;
  const isGroup = !item.to && Boolean(item.children?.length);

  // A group holding the current page starts open, so a reload or a deep link
  // never lands the user inside a collapsed section.
  const containsActive = useMemo(() => holds(item, activePath), [item, activePath]);
  // Each level one step further in, so a sub-group reads as inside its group.
  const indent = collapsed ? undefined : { paddingLeft: `${String(12 + depth * 24)}px` };
  // `defaultOpen` lives on NavGroup only, so narrow before reading it.
  const [open, setOpen] = useState(
    containsActive || ("defaultOpen" in item && item.defaultOpen === true),
  );
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);

  if (isGroup) {
    // Collapsed rail: the group's children appear in a flyout instead of
    // expanding in place, since there is no width to expand into.
    if (collapsed) {
      const firstChild = (item.children ?? []).find((child) => child.to);

      return (
        <RailItem
          label={label}
          trigger={(close) => (
            <button
              type="button"
              aria-label={label}
              aria-haspopup="menu"
              // Clicking a section goes somewhere rather than toggling state:
              // its first reachable child. Hovering still shows the full list.
              onClick={() => {
                close();
                if (!firstChild?.to) return;
                void navigate({ to: firstChild.to });
                onNavigate();
              }}
              className={cn(
                "flex w-full items-center justify-center rounded-md p-2 text-ink-muted hover:bg-surface-sunk hover:text-ink",
                containsActive && "bg-accent-soft text-accent-strong",
              )}
            >
              {Icon && <Icon className="size-4" />}
            </button>
          )}
        >
          {(close) => (
            <>
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                {label}
              </p>
              <ul className="flex flex-col gap-0.5">
                {(item.children ?? []).map((child) => (
                  <NavEntry
                    key={child.labelKey}
                    item={child}
                    activePath={activePath}
                    collapsed={false}
                    onNavigate={() => {
                      close();
                      onNavigate();
                    }}
                  />
                ))}
              </ul>
            </>
          )}
        </RailItem>
      );
    }

    return (
      <li className={depth === 0 ? "mt-1.5" : undefined}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          style={indent}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[13px]",
            "hover:bg-surface-sunk hover:text-ink max-md:py-3",
            // A section of the menu reads a step above its pages.
            depth === 0 ? "font-medium text-ink" : "text-ink-muted",
          )}
        >
          {Icon && <Icon className="size-4 shrink-0" />}
          <span className="flex-1 truncate text-left">{label}</span>
          {open ? (
            <ChevronDown className="size-3.5 shrink-0 opacity-60" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 opacity-60" />
          )}
        </button>

        {open && (
          // A sub-group's items hang off a guide line under its icon.
          <ul
            className={cn("mt-0.5 flex flex-col gap-0.5", depth > 0 && "relative before:absolute before:bottom-1 before:top-1 before:left-[var(--guide)] before:w-px before:bg-line")}
            style={depth > 0 ? ({ "--guide": `${String(12 + depth * 24 + 7)}px` } as CSSProperties) : undefined}
          >
            {(item.children ?? []).map((child) => (
              <NavEntry
                key={child.labelKey}
                item={child}
                activePath={activePath}
                collapsed={collapsed}
                onNavigate={onNavigate}
                depth={depth + 1}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  const link = (onClick: () => void) => (
    <Link
      to={item.to ?? "/"}
      onClick={onClick}
      style={indent}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px]",
        "hover:bg-surface-sunk hover:text-ink max-md:py-3",
        depth === 0 && !collapsed ? "font-medium text-ink" : "text-ink-muted",

        collapsed && "lg:justify-center lg:px-2",
        // Not `activeProps`: the router matches an ancestor route as active, so
        // /pricing/product lit up while you were on /pricing/product/rule.
        item.to === activePath && "bg-accent-soft font-medium text-accent-strong",
      )}
    >
      {Icon && <Icon className="size-4 shrink-0" />}
      <span className={cn("truncate", collapsed && "lg:hidden")}>{label}</span>
    </Link>
  );

  // In the rail the label has nowhere to live, so it appears beside the icon.
  if (collapsed) {
    return (
      <RailItem
        label={label}
        trigger={(close) =>
          link(() => {
            close();
            onNavigate();
          })
        }
      >
        {() => <p className="whitespace-nowrap px-2 py-1 text-[13px] text-ink">{label}</p>}
      </RailItem>
    );
  }

  return <li>{link(onNavigate)}</li>;
}

/**
 * A rail entry and the panel that reveals its label or children.
 *
 * Visibility is state, not `:hover`. CSS hover cannot be dismissed by a click —
 * after navigating, the pointer is still over the item, so the panel would hang
 * around until the mouse moved, and hovering a second item put two menus on
 * screen. Opening on pointer-enter and focus, and closing on pointer-leave,
 * blur or any click inside, keeps exactly one panel up and only while wanted.
 *
 * The panel's left padding belongs to the panel itself, so the pointer crosses
 * from the rail into it without traversing a gap that would close it.
 */
function RailItem({
  label,
  trigger,
  children,
}: {
  label: string;
  trigger: (close: () => void) => ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <li
      className="relative hidden lg:block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        // React's onBlur bubbles, so this fires for anything inside; only
        // close when focus actually left the item and its panel.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
      }}
    >
      {trigger(close)}

      {open && (
        <div className="absolute left-full top-0 z-50 pl-1.5" role="menu" aria-label={label}>
          <div className="min-w-44 rounded-md border border-line bg-surface-over p-1 shadow-overlay">
            {children(close)}
          </div>
        </div>
      )}
    </li>
  );
}

/** Whether a group holds the current page at any depth, so a reload opens every level down to it. */
function holds(item: NavItem, activePath: string | undefined): boolean {
  return (item.children ?? []).some((child) => (child.to != null && child.to === activePath) || holds(child, activePath));
}
